import type {
  TicketDetail,
  TicketMessage,
} from 'containers/Ticketing/_api/types';
import { PlatformType } from 'containers/Ticketing/_api/useCreateNewTicket/types';
import type { TicketCollaborator } from 'containers/Ticketing/_api/useGetCollaborators/types';
import type { ImmerStateCreator } from 'hooks/useStore';

export interface TicketingDetailSlice {
  ticketLayoutId: string;
  selectedThreadId: string;
  selectedTicketId: string;
  selectedBoardId: string;
  selectedBoardName: string;
  selectedTicketDetail: TicketDetail | null;
  selectedViewId: string;
  selectedPlatformTypeTab: PlatformType;
  selectedTicketThreadSubject: string;
  showUnsavedChangesModal: boolean;
  hasUnsavedChanges: boolean;
  resetAllEditors: boolean;
  currentBoardIntakeEmailAddress: string;
  defaultId: string;
  formSubmitAction: boolean;
  openEmailEditor: boolean;
  isCreateNewThread: boolean;
  attachmentName: string | null;
  openSlackCreateEditor: boolean;
  openSlackAuthModal: boolean;
  collaborators: TicketCollaborator[];
  setAttachmentName: (name: string | null) => void;
  setSelectedThreadId: (id: string) => void;
  setSelectedTicketId: (id: string) => void;
  setSelectedTicketDetail: (ticket: TicketDetail) => void;
  setSelectedBoardId: (id: string) => void;
  setSelectedBoardName: (name: string) => void;
  setSelectedViewId: (id: string) => void;
  setSelectedPlatformTypeTab: (platformType: PlatformType) => void;
  selectedTicketMessage?: TicketMessage | null;
  setSelectedTicketMessage: (message: TicketMessage | null) => void;
  setSelectedTicketThreadSubject: (subject: string) => void;
  // eslint-disable-next-line max-len
  setShowUnsavedChangesModal: (show: boolean, external?: boolean) => void; // use external to indicate that action is initiatied from outside Conversations
  setHasUnsavedChanges: (hasUnsavedChanges: boolean) => void;
  setResetAllEditors: (resetAllEditors: boolean) => void;
  setCurrentBoardIntakeEmailAddress: (intakeEmailAddress: string) => void;
  setDefaultId: (defaultId: string) => void;
  setFormSubmitAction: (formSubmitAction: boolean) => void;
  setOpenEmailEditor: (open: boolean) => void;
  setIsCreateNewThread: (isCreateNewThread: boolean) => void;
  setOpenSlackCreateEditor: (open: boolean) => void;
  setOpenSlackAuthModal: (open: boolean) => void;
  setCollaborators: (collaborators: TicketCollaborator[]) => void;
}

export const useTicketingDetailSlice: ImmerStateCreator<
  TicketingDetailSlice
> = (set) => ({
  ticketLayoutId: '',
  selectedThreadId: '',
  selectedTicketId: '',
  selectedBoardId: '',
  selectedBoardName: '',
  selectedTicketDetail: null,
  selectedViewId: '',
  selectedPlatformTypeTab: PlatformType.ALL,
  selectedTicketMessage: null,
  selectedTicketThreadSubject: '',
  showUnsavedChangesModal: false,
  hasUnsavedChanges: false,
  resetAllEditors: false,
  currentBoardIntakeEmailAddress: '',
  defaultId: '',
  formSubmitAction: false,
  openEmailEditor: false,
  isCreateNewThread: false,
  attachmentName: null,
  openSlackCreateEditor: false,
  openSlackAuthModal: false,
  collaborators: [],
  setAttachmentName: (name: string | null) =>
    set(
      (state) => {
        state.ticketingDetailSlice.attachmentName = name;
      },
      false,
      'setAttachmentName'
    ),
  setSelectedPlatformTypeTab: (platformType: PlatformType) =>
    set(
      (state) => {
        state.ticketingDetailSlice.selectedPlatformTypeTab = platformType;
      },
      false,
      'setSelectedPlatformType'
    ),
  setSelectedThreadId: (id: string) =>
    set(
      (state) => {
        state.ticketingDetailSlice.selectedThreadId = id;
      },
      false,
      'setSelectedThreadId'
    ),
  setSelectedTicketId: (id: string) =>
    set(
      (state) => {
        // Reset thread-scoped state when switching to a different ticket so
        // downstream consumers (e.g. MatterSubNav preserving :threadId across
        // tab switches) never re-apply a threadId from a prior ticket.
        if (state.ticketingDetailSlice.selectedTicketId !== id) {
          state.ticketingDetailSlice.selectedThreadId = '';
          state.ticketingDetailSlice.selectedTicketThreadSubject = '';
        }
        state.ticketingDetailSlice.selectedTicketId = id;
      },
      false,
      'setSelectedTicketId'
    ),
  setSelectedTicketDetail: (ticketDetail: TicketDetail) =>
    set(
      (state) => {
        state.ticketingDetailSlice.selectedTicketDetail = ticketDetail;
      },
      false,
      'setSelectedTicketDetail'
    ),
  setSelectedBoardId: (id: string) =>
    set(
      (state) => {
        state.ticketingDetailSlice.selectedBoardId = id;
      },
      false,
      'setSelectedBoardId'
    ),
  setSelectedBoardName: (name: string) =>
    set(
      (state) => {
        state.ticketingDetailSlice.selectedBoardName = name;
      },
      false,
      'setSelectedBoardName'
    ),
  setSelectedViewId: (id: string) =>
    set(
      (state) => {
        state.ticketingDetailSlice.selectedViewId = id;
      },
      false,
      'setSelectedViewId'
    ),
  setSelectedTicketMessage: (message: TicketMessage | null) =>
    set(
      (state) => {
        state.ticketingDetailSlice.selectedTicketMessage = message;
      },
      false,
      'setSelectedTicketMessage'
    ),
  setSelectedTicketThreadSubject: (subject: string) =>
    set(
      (state) => {
        state.ticketingDetailSlice.selectedTicketThreadSubject = subject;
      },
      false,
      'setSelectedTicketThreadSubject'
    ),
  setShowUnsavedChangesModal: (show: boolean, external = false) =>
    set(
      (state) => {
        state.ticketingDetailSlice.showUnsavedChangesModal = show;
        state.ticketingDetailSlice.resetAllEditors = external;
      },
      false,
      'setShowUnsavedChangesModal'
    ),
  setHasUnsavedChanges: (hasUnsavedChanges: boolean) =>
    set(
      (state) => {
        state.ticketingDetailSlice.hasUnsavedChanges = hasUnsavedChanges;
      },
      false,
      'setHasUnsavedChanges'
    ),
  setResetAllEditors: (resetAllEditors: boolean) =>
    set(
      (state) => {
        state.ticketingDetailSlice.resetAllEditors = resetAllEditors;
      },
      false,
      'setResetAllEditors'
    ),
  setCurrentBoardIntakeEmailAddress: (intakeEmailAddress: string) =>
    set(
      (state) => {
        state.ticketingDetailSlice.currentBoardIntakeEmailAddress =
          intakeEmailAddress;
      },
      false,
      'setCurrentBoardIntakeEmailAddress'
    ),
  setDefaultId: (defaultId: string) =>
    set(
      (state) => {
        state.ticketingDetailSlice.defaultId = defaultId;
      },
      false,
      'setDefaultId'
    ),
  setFormSubmitAction: (formSubmitAction: boolean) =>
    set(
      (state) => {
        state.ticketingDetailSlice.formSubmitAction = formSubmitAction;
      },
      false,
      'setFormSubmitAction'
    ),
  setOpenEmailEditor: (open: boolean) =>
    set(
      (state) => {
        state.ticketingDetailSlice.openEmailEditor = open;
      },
      false,
      'setOpenEmailEditor'
    ),
  setIsCreateNewThread: (isCreateNewThread: boolean) =>
    set(
      (state) => {
        state.ticketingDetailSlice.isCreateNewThread = isCreateNewThread;
      },
      false,
      'setIsCreateNewThread'
    ),
  setOpenSlackCreateEditor: (open: boolean) =>
    set(
      (state) => {
        state.ticketingDetailSlice.openSlackCreateEditor = open;
      },
      false,
      'setOpenSlackCreateEditor'
    ),
  setOpenSlackAuthModal: (open: boolean) =>
    set(
      (state) => {
        state.ticketingDetailSlice.openSlackAuthModal = open;
      },
      false,
      'setOpenSlackAuthModal'
    ),
  setCollaborators: (collaborators: TicketCollaborator[]) =>
    set(
      (state) => {
        state.ticketingDetailSlice.collaborators = collaborators;
      },
      false,
      'setCollaborators'
    ),
});
